import { jsxs, Fragment, jsx } from "react/jsx-runtime";
import { forwardRef, useContext, useRef, useMemo, createElement, useState, isValidElement, cloneElement, createContext } from "react";
import { withThemeAndSize } from "./components/withThemeAndSize/index.js";
import { cn as clsx } from "./helpers/react.js";
import { Icons24 } from "./components/Icons24Sprite/index.js";
import { isFunction } from "./helpers/utils.js";
import { Font } from "./components/Font/index.js";
import { IconWrapper } from "./components/IconWrapper/index.js";
import { THEME } from "./constants/theme.js";
import { pickIntent } from "./helpers/ui.js";
import { Header } from "./components/OptionList/Header/index.js";
import './assets/index66.css';const optionItem = "_optionItem_1wc41_2";
const dragging = "_dragging_1wc41_21";
const withTheme = "_withTheme_1wc41_28";
const disabled = "_disabled_1wc41_49";
const secondary = "_secondary_1wc41_57";
const active = "_active_1wc41_76";
const critical = "_critical_1wc41_83";
const warning = "_warning_1wc41_118";
const leftIcon = "_leftIcon_1wc41_153";
const rightIcon = "_rightIcon_1wc41_154";
const contentContainer = "_contentContainer_1wc41_164";
const content = "_content_1wc41_164";
const label = "_label_1wc41_183";
const caption = "_caption_1wc41_202";
const rightSection = "_rightSection_1wc41_217";
const fill = "_fill_1wc41_223";
const uppercase = "_uppercase_1wc41_230";
const s$2 = {
  optionItem,
  dragging,
  withTheme,
  disabled,
  secondary,
  active,
  critical,
  warning,
  leftIcon,
  rightIcon,
  contentContainer,
  content,
  label,
  caption,
  rightSection,
  fill,
  uppercase
};
const OPTION_ITEM_INTENT = pickIntent(["secondary", "critical", "warning"]);
const INTENT_CLASSNAME_MAP = {
  [OPTION_ITEM_INTENT.SECONDARY]: s$2.secondary,
  [OPTION_ITEM_INTENT.CRITICAL]: s$2.critical,
  [OPTION_ITEM_INTENT.WARNING]: s$2.warning
};
const getElementTreeInfo = (ref) => {
  if (!ref || !("current" in ref) || !ref.current) {
    return null;
  }
  const container = ref.current.parentElement;
  if (!container) {
    return null;
  }
  const siblings = Array.from(container.children);
  return {
    container,
    siblings,
    index: siblings.indexOf(ref.current),
    element: ref.current,
    siblingsRects: siblings.map((sibling) => sibling.getBoundingClientRect())
  };
};
const OptionItem = withThemeAndSize(
  forwardRef((props, ref) => {
    const dragContext = useContext(OptionListDragContext);
    const {
      className,
      children,
      label: label2,
      caption: caption2,
      disabled: disabled2,
      contentContainerClassName,
      contentClassName,
      icon,
      rightIcon: rightIcon2,
      intent = OPTION_ITEM_INTENT.SECONDARY,
      fill: fill2 = false,
      uppercase: uppercase2 = false,
      as = "button",
      theme,
      size: _size,
      role = "menuitem",
      tabIndex = props.as === "label" ? void 0 : 0,
      rightSection: rightSection2,
      active: active2,
      onDragged,
      ...rest
    } = props;
    const hasTheme = theme !== THEME.CUSTOM;
    const extraRef = useRef(null);
    if (!ref) {
      ref = extraRef;
    }
    const componentProps = {
      ...rest,
      ref,
      role,
      tabIndex,
      className: clsx(
        s$2.optionItem,
        {
          [s$2.active]: active2,
          [s$2.disabled]: disabled2,
          [s$2.fill]: fill2,
          [s$2.uppercase]: uppercase2
        },
        hasTheme && {
          [s$2.withTheme]: true,
          [INTENT_CLASSNAME_MAP[intent]]: true
        },
        className
      )
    };
    const onMouseDown = (event) => {
      const treeInfo = getElementTreeInfo(ref);
      if (!treeInfo) {
        return;
      }
      const { container, siblings, index: originalIndex, element, siblingsRects } = treeInfo;
      let hoveredSiblingIndex = originalIndex;
      const onMouseMove = ({ clientY }) => {
        const isMouseOutsideOfContainer = clientY < container.getBoundingClientRect().top || clientY > container.getBoundingClientRect().bottom;
        if (isMouseOutsideOfContainer) {
          return;
        }
        element.classList.add(s$2.dragging);
        const newIndex = siblingsRects.findIndex((siblingRect) => {
          return siblingRect.top <= clientY && siblingRect.bottom >= clientY;
        });
        if (newIndex === -1) {
          return;
        }
        hoveredSiblingIndex = newIndex;
        const dragInfo = hoveredSiblingIndex === originalIndex ? null : {
          oldIndex: originalIndex,
          newIndex: hoveredSiblingIndex,
          siblingsRects,
          siblings
        };
        dragContext == null ? void 0 : dragContext.setDragInfo(dragInfo);
      };
      const onMouseUp = (event2) => {
        if (hoveredSiblingIndex !== originalIndex) {
          onDragged == null ? void 0 : onDragged(hoveredSiblingIndex);
          dragContext == null ? void 0 : dragContext.setDragInfo(null);
        }
        element.classList.remove(s$2.dragging);
        document.removeEventListener("mousemove", onMouseMove);
        document.removeEventListener("mouseup", onMouseUp);
      };
      document.addEventListener("mousemove", onMouseMove);
      document.addEventListener("mouseup", onMouseUp);
    };
    const offsetY = useMemo(() => {
      if (!(dragContext == null ? void 0 : dragContext.dragInfo)) {
        return 0;
      }
      const treeInfo = getElementTreeInfo(ref);
      if (!treeInfo) {
        return 0;
      }
      const { oldIndex, newIndex, siblingsRects } = dragContext.dragInfo;
      const { index: currentIndex } = treeInfo;
      if (currentIndex === oldIndex) {
        return siblingsRects[newIndex].top - siblingsRects[oldIndex].top;
      } else if (currentIndex <= Math.max(oldIndex, newIndex) && currentIndex >= Math.min(oldIndex, newIndex)) {
        return newIndex > oldIndex ? siblingsRects[currentIndex - 1].top - siblingsRects[currentIndex].top : siblingsRects[currentIndex + 1].top - siblingsRects[currentIndex].top;
      }
    }, [dragContext == null ? void 0 : dragContext.dragInfo]);
    if (offsetY) {
      componentProps.style = {
        ...componentProps.style,
        transform: `translateY(${offsetY}px)`
      };
    }
    if (as === "button") {
      componentProps.disabled = disabled2;
    }
    const content2 = /* @__PURE__ */ jsxs(Fragment, { children: [
      dragContext && /* @__PURE__ */ jsx(IconWrapper, { size: "md", className: s$2.leftIcon, children: /* @__PURE__ */ jsx(Icons24, { name: "Draggable", onMouseDown }) }),
      /* @__PURE__ */ jsx(IconWrapper, { size: "md", className: s$2.leftIcon, children: icon }),
      /* @__PURE__ */ jsxs("span", { className: clsx(s$2.contentContainer, contentContainerClassName), children: [
        (children || label2) && /* @__PURE__ */ jsx(Font, { type: "text", size: 14, weight: "medium", children: /* @__PURE__ */ jsxs("span", { className: clsx(s$2.content, contentClassName), children: [
          label2 && /* @__PURE__ */ jsx("span", { className: s$2.label, children: label2 }),
          isFunction(children) ? children(componentProps) : children
        ] }) }),
        caption2 && /* @__PURE__ */ jsx(Font, { type: "text", size: 12, weight: "medium", children: /* @__PURE__ */ jsx("span", { className: s$2.caption, children: caption2 }) })
      ] }),
      /* @__PURE__ */ jsx(IconWrapper, { size: "md", className: s$2.rightIcon, children: rightIcon2 }),
      rightSection2
    ] });
    return createElement(as, componentProps, content2);
  })
);
OptionItem.displayName = "OptionItem";
const group = "_group_1q51n_1";
const forceHideBorder = "_forceHideBorder_1q51n_13";
const forceShowBorder = "_forceShowBorder_1q51n_16";
const s$1 = {
  group,
  forceHideBorder,
  forceShowBorder
};
const Group = withThemeAndSize(
  forwardRef(
    ({
      size,
      theme,
      children,
      className,
      role = "group",
      forceShowBorder: forceShowBorder2,
      forceHideBorder: forceHideBorder2,
      asFragment,
      expandable,
      onExpandChange,
      defaultExpanded = true,
      expandableItemProps: { icon, onClick, ...restExpandableItemProps } = {},
      ...restHtmlProps
    }, ref) => {
      var _a, _b;
      const [isExpanded, setIsExpanded] = useState(defaultExpanded);
      if (!children) {
        return null;
      }
      if (asFragment) {
        return /* @__PURE__ */ jsx(Fragment, { children });
      }
      const toggleIsExpanded = () => {
        setIsExpanded(!isExpanded);
        onExpandChange == null ? void 0 : onExpandChange(!isExpanded);
      };
      const handleKeyDown = (event) => {
        if (event.target === event.currentTarget && (event.key === "Enter" || event.key === " ")) {
          event.preventDefault();
          event.stopPropagation();
          toggleIsExpanded();
        }
      };
      const isHidden = expandable && !isExpanded;
      const derivedIcon = isValidElement(icon) && (isFunction((_a = icon.props) == null ? void 0 : _a.onClick) || isFunction((_b = icon.props) == null ? void 0 : _b.onChange)) ? cloneElement(icon, {
        onClick: (event) => {
          var _a2, _b2;
          event.stopPropagation();
          (_b2 = (_a2 = icon.props).onClick) == null ? void 0 : _b2.call(_a2, event);
          onClick == null ? void 0 : onClick(event);
        }
      }) : icon;
      return /* @__PURE__ */ jsxs(Fragment, { children: [
        expandable && /* @__PURE__ */ jsx(
          OptionItem,
          {
            ...restExpandableItemProps,
            as: "div",
            onClick: (event) => {
              if (isFunction(onClick) && onClick(event) === false) {
                return;
              }
              toggleIsExpanded();
            },
            onKeyDown: handleKeyDown,
            rightIcon: /* @__PURE__ */ jsx(Icons24, { name: isExpanded ? "ArrowDropUpFill" : "ArrowDropDownFill", size: 20 }),
            icon: derivedIcon
          }
        ),
        /* @__PURE__ */ jsx(
          "div",
          {
            ...restHtmlProps,
            role,
            className: clsx(
              s$1.group,
              {
                [s$1.forceShowBorder]: forceShowBorder2,
                [s$1.forceHideBorder]: forceHideBorder2
              },
              className
            ),
            hidden: isHidden,
            ref,
            children
          }
        )
      ] });
    }
  )
);
const list = "_list_1skjy_1";
const optionListItemGroup = "_optionListItemGroup_1skjy_16";
const s = {
  list,
  optionListItemGroup
};
const OptionListDragContext = createContext(null);
const ListDragContainer = (props) => {
  const { children } = props;
  const [dragInfo, setDragInfo] = useState(null);
  const contextValue = useMemo(
    () => ({
      dragInfo,
      setDragInfo
    }),
    [dragInfo, setDragInfo]
  );
  return /* @__PURE__ */ jsx(OptionListDragContext.Provider, { value: contextValue, children });
};
const List = withThemeAndSize(
  forwardRef(
    ({ className, theme, children, role = "menu", ...restHtmlProps }, ref) => {
      if (!children) {
        return null;
      }
      return /* @__PURE__ */ jsx("div", { ...restHtmlProps, ref, className: clsx(s.list, className), role, children });
    }
  )
);
List.displayName = "OptionList";
const OptionList = Object.assign(List, {
  Group,
  ListDragContainer,
  Header,
  Item: OptionItem
});
export {
  Group as G,
  INTENT_CLASSNAME_MAP as I,
  ListDragContainer as L,
  OptionList as O,
  OptionItem as a,
  OPTION_ITEM_INTENT as b,
  OptionListDragContext as c
};
